import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiTrash2 } from "react-icons/fi";

import api from "../../services/api";
import Loader from "../../components/loader";

export default function ConnectedApps() {
  const [grants, setGrants] = useState(null);
  const [revoking, setRevoking] = useState(null);

  async function load() {
    try {
      const r = await api.get("/oauth/grants");
      if (!r.ok) throw new Error(r.code);
      setGrants(r.data);
    } catch (e) {
      toast.error("Failed to load connected apps");
      setGrants([]);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function revoke(id, name) {
    if (!window.confirm(`Revoke access for ${name}? Any active session for this app will stop working shortly.`)) return;
    setRevoking(id);
    try {
      const r = await api.remove(`/oauth/grants/${id}`);
      if (!r.ok) throw new Error(r.code);
      toast.success("Access revoked");
      await load();
    } catch (e) {
      toast.error("Failed to revoke");
    }
    setRevoking(null);
  }

  if (!grants) return <Loader />;

  return (
    <div className="w-full min-h-[90vh] bg-gray-100 p-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h1 className="text-lg font-semibold text-gray-900">Connected apps</h1>
          <p className="text-sm text-gray-500 mt-1">
            Applications that have access to your accounting account via MCP (Claude, Cursor, etc.).
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {grants.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No connected apps yet.</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {grants.map((g) => (
                <li key={g._id} className="flex items-center gap-4 p-4">
                  {g.logo_uri ? (
                    <img src={g.logo_uri} alt={g.client_name} className="w-10 h-10 rounded-md object-contain bg-gray-50 border border-gray-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-sm font-semibold text-gray-500">
                      {(g.client_name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{g.client_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Connected {formatDate(g.created_at)}
                      {g.last_used_at && <> · Last used {formatDate(g.last_used_at)}</>}
                    </div>
                    {g.scopes?.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {g.scopes.map((s) => (
                          <code key={s} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {s}
                          </code>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => revoke(g._id, g.client_name)}
                    disabled={revoking === g._id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50">
                    <FiTrash2 className="w-4 h-4" />
                    {revoking === g._id ? "Revoking…" : "Revoke"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-xs text-gray-500 px-1">
          Revocation takes effect immediately for new sessions. An active session may continue working for up to 1 hour until its access token expires.
        </div>
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return date.toLocaleDateString();
}
